import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, Folder, FileText, Tag, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input, Badge } from "@/components/common";

export default function GlobalSearch({ workspaceId, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    if (!q.trim()) { setRes(null); return; }
    setBusy(true);
    try { const { data } = await api.get(`/search?workspace_id=${workspaceId}&q=${encodeURIComponent(q)}`); setRes(data); }
    catch { setRes(null); } finally { setBusy(false); }
  }, [q, workspaceId]);
  useEffect(() => { const t = setTimeout(run, 250); return () => clearTimeout(t); }, [run]);

  const go = (path) => { onClose(); navigate(path); };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh]" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="w-full max-w-2xl" style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 4 }} onClick={(e) => e.stopPropagation()} data-testid="global-search-modal">
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
          <Search size={16} style={{ color: "var(--si-text-2)" }} />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts, groups, notes, profile fields…" data-testid="global-search-input" style={{ border: "none", background: "transparent" }} />
          <button onClick={onClose} data-testid="global-search-close"><X size={18} style={{ color: "var(--si-text-2)" }} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {!q.trim() && <div className="text-sm py-8 text-center" style={{ color: "var(--si-text-2)" }}>Type to search across your workspace.</div>}
          {q.trim() && busy && <div className="text-sm py-6 text-center si-label">Searching…</div>}
          {res && (
            <div className="space-y-4">
              {(res.groups || []).length > 0 && (
                <div>
                  <div className="si-label px-2 mb-1 flex items-center gap-1"><Folder size={11} /> Groups</div>
                  {res.groups.map((g) => (
                    <button key={g.id} onClick={() => go(`/app/groups?group=${g.id}`)} className="w-full text-left px-2 py-2 text-sm rounded transition-colors duration-200 hover:bg-[var(--si-surface-hover)]" style={{ color: "var(--si-text)" }} data-testid={`search-group-${g.id}`}>
                      {g.name} <span className="si-label ml-1">{(g.folder_path || []).map((p) => p.name).join(" / ")} · {g.member_count} members</span>
                    </button>
                  ))}
                </div>
              )}
              {(res.entities || []).length > 0 && (
                <div>
                  <div className="si-label px-2 mb-1 flex items-center gap-1"><Users size={11} /> Contacts</div>
                  {res.entities.map((e) => (
                    <button key={e.id} onClick={() => go(`/app/entities/${e.id}`)} className="w-full text-left px-2 py-2 text-sm rounded transition-colors duration-200 hover:bg-[var(--si-surface-hover)]" style={{ color: "var(--si-text)" }} data-testid={`search-entity-${e.id}`}>
                      <span>{e.name}</span> <span className="si-label ml-1">{e.type}</span>
                      {(e.groups || []).length > 0 && <span className="ml-2">{e.groups.map((gn) => <Badge key={gn} value="candidate">{gn}</Badge>)}</span>}
                    </button>
                  ))}
                </div>
              )}
              {(res.notes || []).length > 0 && (
                <div>
                  <div className="si-label px-2 mb-1 flex items-center gap-1"><FileText size={11} /> Notes</div>
                  {res.notes.map((n) => (
                    <button key={n.id} onClick={() => go(`/app/entities/${(n.entity_ids || [])[0] || ""}`)} className="w-full text-left px-2 py-2 text-xs rounded transition-colors duration-200 hover:bg-[var(--si-surface-hover)]" style={{ color: "var(--si-text-2)" }} data-testid={`search-note-${n.id}`}>
                      <span style={{ color: "var(--si-text)" }}>{n.title || n.category}</span> — {n.body}
                    </button>
                  ))}
                </div>
              )}
              {(res.fields || []).length > 0 && (
                <div>
                  <div className="si-label px-2 mb-1 flex items-center gap-1"><Tag size={11} /> Profile fields</div>
                  {res.fields.map((f, i) => (
                    <button key={i} onClick={() => go(`/app/entities/${f.entity_id}`)} className="w-full text-left px-2 py-2 text-xs rounded transition-colors duration-200 hover:bg-[var(--si-surface-hover)]" style={{ color: "var(--si-text-2)" }} data-testid={`search-field-${i}`}>
                      <span style={{ color: "var(--si-text)" }}>{f.entity_name}</span> · {f.label}: {f.value}
                    </button>
                  ))}
                </div>
              )}
              {!busy && !(res.groups || []).length && !(res.entities || []).length && !(res.notes || []).length && !(res.fields || []).length && (
                <div className="text-sm py-8 text-center" style={{ color: "var(--si-text-2)" }}>No matches for “{q}”.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
